/** Terminal chrome navigation — inspection layers around a running trader. */

/** Global operating mode. Manual/Assisted only appear after Take control on Trade. */
export type TerminalMode = 'manual' | 'assisted' | 'auto';

/** Primary left-rail destinations. Home is the default trader briefing. */
export type NavId =
  | 'home'
  | 'trade'
  | 'agent'
  | 'strategies'
  | 'automation'
  | 'signals'
  | 'portfolio'
  | 'challenges'
  | 'analytics'
  | 'wallets'
  | 'learning'
  | 'help'
  | 'settings';

/** In-page chips on the Portfolio rail (Account / Performance / Backtests). */
export type PortfolioWorkspaceTab = 'account' | 'performance' | 'fleet';

/** In-page tabs on the Strategies rail (Running stack / Library / Discover / Optimizer). Autopilot stays a deep-link under Running. */
export type StrategyWorkspaceTab = 'live' | 'browse' | 'discover' | 'optimizer' | 'autopilot';

export const STRATEGY_WORKSPACE_TABS: { id: StrategyWorkspaceTab; label: string }[] = [
  { id: 'live', label: 'Running' },
  { id: 'browse', label: 'Library' },
  { id: 'discover', label: 'Discover' },
  { id: 'optimizer', label: 'Optimizer' },
];

export function isLiveStrategyTab(value: string | null | undefined): boolean {
  return value === 'live' || value === 'autopilot';
}

export function isLibraryStrategyTab(value: string | null | undefined): boolean {
  return value === 'browse' || value === 'discover' || value === 'optimizer';
}

/** In-page tabs on the Settings rail. */
export type SettingsWorkspaceTab = 'account' | 'venues' | 'trading' | 'providers';

export const SETTINGS_WORKSPACE_TABS: { id: SettingsWorkspaceTab; label: string }[] = [
  { id: 'account', label: 'Account' },
  { id: 'venues', label: 'Venues' },
  { id: 'trading', label: 'Trading' },
  { id: 'providers', label: 'Providers' },
];

/** Overlay utility. Account lives on the top-bar avatar; the Ask drawer lives in More. */
export type RailUtilityId = 'chat';

export type OverlayId = 'risk' | 'account' | 'agent';

export type MoreNavItemId = NavId | RailUtilityId;

export type MoreNavGroup = {
  id: string;
  label: string;
  items: MoreNavItemId[];
};

/**
 * Desktop rail — Home, Trade, Strategies, then inspection layers.
 * Console (`automation`) is the Running tab of Strategies (deep link `?nav=strategies&stTab=live`);
 * it is not a separate destination and does not appear in More.
 */
export const DESKTOP_PRIMARY_NAV: NavId[] = [
  'home',
  'trade',
  'strategies',
  'signals',
  'portfolio',
  'agent',
];

/** Phone tab bar — Home, Trade, Strategies, Positions, Agent. */
export const MOBILE_PRIMARY_NAV: NavId[] = ['home', 'trade', 'strategies', 'portfolio', 'agent'];

/** Desktop chrome. Prefer DESKTOP_PRIMARY_NAV / MOBILE_PRIMARY_NAV when the surface matters. */
export const PRIMARY_NAV = DESKTOP_PRIMARY_NAV;

const MORE_NAV_GROUP_DEFS: MoreNavGroup[] = [
  { id: 'inspect', label: 'Inspect', items: ['signals', 'portfolio', 'strategies', 'challenges'] },
  { id: 'library', label: 'Library', items: ['wallets', 'learning'] },
  { id: 'account', label: 'Account', items: ['chat', 'settings', 'help'] },
];

/** Overflow for a given chrome set (desktop rail vs phone tab bar). */
export function moreNavGroupsFor(primary: readonly NavId[]): MoreNavGroup[] {
  const hidden = new Set<string>(primary);
  return MORE_NAV_GROUP_DEFS.map((group) => ({
    ...group,
    items: group.items.filter((id) => id === 'chat' || !hidden.has(id)),
  })).filter((group) => group.items.length > 0);
}

export const MORE_NAV_GROUPS: MoreNavGroup[] = moreNavGroupsFor(DESKTOP_PRIMARY_NAV);

export function moreNavItemIds(primary: readonly NavId[] = DESKTOP_PRIMARY_NAV): MoreNavItemId[] {
  return moreNavGroupsFor(primary).flatMap((group) => group.items);
}

/** Rail labels: Home is the trader; Trade and Strategies follow. */
export const TERMINAL_NAV: { id: NavId; label: string }[] = [
  { id: 'home', label: 'Home' },
  { id: 'trade', label: 'Trade' },
  { id: 'strategies', label: 'Strategies' },
  { id: 'signals', label: 'Activity' },
  { id: 'portfolio', label: 'Positions' },
  { id: 'agent', label: 'Ask' },
  { id: 'automation', label: 'Running' },
  { id: 'challenges', label: 'Challenges' },
  { id: 'wallets', label: 'Explorer' },
  { id: 'learning', label: 'Learn' },
  { id: 'help', label: 'Help' },
  { id: 'settings', label: 'Settings' },
];

/** The chat drawer is the same conversation as the Ask page — one name everywhere. */
export const TERMINAL_RAIL_UTILS: { id: RailUtilityId; label: string }[] = [
  { id: 'chat', label: 'Ask Oroboros' },
];

export function navItemLabel(id: MoreNavItemId): string {
  if (id === 'chat') {
    return TERMINAL_RAIL_UTILS.find((item) => item.id === id)?.label ?? 'Ask Oroboros';
  }
  return TERMINAL_NAV.find((item) => item.id === id)?.label ?? id;
}

export function isPrimaryNav(
  id: string | null | undefined,
  surface: 'desktop' | 'mobile' | 'any' = 'any',
): id is NavId {
  if (id == null) return false;
  if (surface === 'mobile') return (MOBILE_PRIMARY_NAV as string[]).includes(id);
  if (surface === 'desktop') return (DESKTOP_PRIMARY_NAV as string[]).includes(id);
  return (
    (DESKTOP_PRIMARY_NAV as string[]).includes(id) || (MOBILE_PRIMARY_NAV as string[]).includes(id)
  );
}

/** All destinations remain addressable (deep links, More sheet, Challenges connect). */
export function visibleTerminalNav(_opts?: { proprConnected?: boolean }): typeof TERMINAL_NAV {
  return TERMINAL_NAV;
}

/** Persistent chrome items in desktop rail order. */
export function primaryTerminalNav(
  surface: 'desktop' | 'mobile' = 'desktop',
): { id: NavId; label: string }[] {
  const ids = surface === 'mobile' ? MOBILE_PRIMARY_NAV : DESKTOP_PRIMARY_NAV;
  return ids.map((id) => {
    const item = TERMINAL_NAV.find((entry) => entry.id === id);
    return item ?? { id, label: navItemLabel(id) };
  });
}

/** Destinations that render the live Trade shell (equity / positions / market). */
export const LIVE_NAV_IDS: NavId[] = ['trade'];

const NAV_IDS = new Set<string>(TERMINAL_NAV.map((n) => n.id));

/** Legacy rails remain deep-link aliases onto the merged workspaces. */
const NAV_ALIASES: Record<string, NavId> = {
  analytics: 'portfolio',
};

export function canonicalizeNavId(value: string | null | undefined): NavId | null {
  if (!value) return null;
  const mapped = NAV_ALIASES[value] ?? value;
  return NAV_IDS.has(mapped) ? (mapped as NavId) : null;
}

export function isNavId(value: string | null | undefined): value is NavId {
  return canonicalizeNavId(value) != null || value === 'analytics' || value === 'automation';
}

export function isPortfolioWorkspaceTab(
  value: string | null | undefined,
): value is PortfolioWorkspaceTab {
  return value === 'account' || value === 'performance' || value === 'fleet';
}

export function isStrategyWorkspaceTab(
  value: string | null | undefined,
): value is StrategyWorkspaceTab {
  return (
    value === 'live' ||
    value === 'browse' ||
    value === 'discover' ||
    value === 'optimizer' ||
    value === 'autopilot'
  );
}

export function isSettingsWorkspaceTab(
  value: string | null | undefined,
): value is SettingsWorkspaceTab {
  return value === 'account' || value === 'venues' || value === 'trading' || value === 'providers';
}

/** Map `openSettings(focus)` / `?settingsTab=` onto a Settings tab. `propr` lands on Venues. */
export function settingsTabFromFocus(
  focus: string | null | undefined,
): SettingsWorkspaceTab | null {
  if (isSettingsWorkspaceTab(focus)) return focus;
  if (focus === 'propr') return 'venues';
  return null;
}

/** Deep-link into a terminal rail destination (`/app?nav=strategies`). */
export function terminalNavHref(terminalPath: string, nav: NavId): string {
  const base = String(terminalPath || '/app').split('?')[0];
  const canonical = canonicalizeNavId(nav) || nav;
  const href = `${base}?nav=${encodeURIComponent(canonical)}`;
  if (nav === 'analytics') return `${href}&pfTab=performance`;
  return href;
}

/** Prefer `?nav=` from the URL; used for landing → terminal deep links. */
export function readNavFromLocation(
  search = typeof window !== 'undefined' ? window.location.search : '',
): NavId | null {
  try {
    const raw = new URLSearchParams(search).get('nav');
    return canonicalizeNavId(raw);
  } catch {
    return null;
  }
}

/** Portfolio chip from `?pfTab=` or the legacy `?nav=analytics` alias. */
export function readPortfolioWorkspaceTab(
  search = typeof window !== 'undefined' ? window.location.search : '',
): PortfolioWorkspaceTab | null {
  try {
    const params = new URLSearchParams(search);
    if (params.get('nav') === 'analytics') return 'performance';
    const tab = params.get('pfTab');
    return isPortfolioWorkspaceTab(tab) ? tab : null;
  } catch {
    return null;
  }
}

/** Strategies chip from `?stTab=` or the legacy `?nav=automation` alias. */
export function readStrategyWorkspaceTab(
  search = typeof window !== 'undefined' ? window.location.search : '',
): StrategyWorkspaceTab | null {
  try {
    const params = new URLSearchParams(search);
    if (params.get('nav') === 'automation') {
      return params.get('agentTab') === 'autopilot' ? 'autopilot' : 'live';
    }
    const tab = params.get('stTab');
    return isStrategyWorkspaceTab(tab) ? tab : null;
  } catch {
    return null;
  }
}

/** Settings tab from `?settingsTab=`. */
export function readSettingsWorkspaceTab(
  search = typeof window !== 'undefined' ? window.location.search : '',
): SettingsWorkspaceTab | null {
  try {
    const tab = new URLSearchParams(search).get('settingsTab');
    return isSettingsWorkspaceTab(tab) ? tab : null;
  } catch {
    return null;
  }
}
